<template>
  <section class="featuresSection">
    <h1 class="featuresSectionTitle">Some of our features</h1>

    <div class="featuresSectionContent">
      <InfographicSection
        :button-class-name="'btn-round-yellow'"
        :button-text="'Get started'"
        :has-white-text="true"
        :on-button-click="() => onRedirect('PodcasterRegister')"
        :paragraph="'Record your episode on your phone or in your favorite studio and use our creator tools to produce, distribute to and grow your audience.'"
        :title="'Edit and publish your podcast all in one place'"
        class="featuresInfoSection"
      >
        <div class="videoContainer">
          <video
            v-if="isNarrowViewport"
            :poster="`static/videos/audio-editor-poster.720p.q60.webp`"
            loop
            muted
            playsinline
            preload="none"
          >
            <source :src="`static/videos/audio-editor.720p.av1.qp100.mp4`" type="video/mp4; codecs=av01.0.05M.08" />
            <source :src="`static/videos/audio-editor.720p.hevc.crf28.mp4`" type="video/mp4; codecs=hvc1" />
            <source :src="`static/videos/audio-editor.720p.avc.crf28.mp4`" type="video/mp4; codecs=avc1.4D401E" />
          </video>
          <video
            v-else
            :poster="`static/videos/audio-editor-poster.720p.q60.webp`"
            loop
            muted
            playsinline
            preload="none"
          >
            <source :src="`static/videos/audio-editor.1080p.av1.qp80.mp4`" type="video/mp4; codecs=av01.0.05M.08" />
            <source :src="`static/videos/audio-editor.1080p.hevc.crf24.mp4`" type="video/mp4; codecs=hvc1" />
            <source :src="`static/videos/audio-editor.1080p.avc.crf24.mp4`" type="video/mp4; codecs=avc1.4D401E" />
          </video>
        </div>
      </InfographicSection>
    </div>
  </section>
</template>

<script setup>
import { useMediaQuery } from '@vueuse/core'
import { useRouter } from 'vue-router/composables'
import { bool } from 'vue-types'

import InfographicSection from 'components/reused-components/infographicSection/InfographicSection'

defineProps({
  isNarrowViewport: bool().isRequired,
})

const router = useRouter()

const onRedirect = (routeName) => {
  router.push({ name: routeName })
}

const isNarrowViewport = useMediaQuery('(max-width: 767px)')
</script>

<style lang="scss" scoped>
@import 'assets/scss/base/bootstrap_variables';
@import 'assets/scss/base/functions';

.featuresSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1440px;
  padding: 0 16px;
  margin: 88px auto 0;

  @media (min-width: $screen-sm-min) {
    padding: 0 32px;
    margin: 120px auto 0;
  }

  .featuresSectionTitle {
    font-size: to-rem(34);
    font-weight: 400;
    line-height: 1.1;
    color: inherit;
    text-align: center;

    @media (min-width: $screen-sm-min) {
      font-size: to-rem(52);
    }
  }

  .featuresSectionContent {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;

    @media (min-width: $screen-sm-min) {
      justify-content: unset;
    }

    .featuresInfoSection {
      width: 100vw;
      margin-top: 56px;
      margin-left: -14.5px;

      @media (min-width: $screen-sm-min) {
        width: unset;
        margin-top: 80px;
        margin-left: unset;
      }
    }

    .videoContainer {
      position: relative;
      right: 50%;
      left: 50%;
      width: 100vw;
      aspect-ratio: 710/448;
      margin-right: -50vw;
      margin-left: -50vw;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='710' height='448' fill='none'%3E%3Cg filter='url(%23a)'%3E%3Cellipse cx='354.842' cy='399.991' fill='%23000' rx='310.196' ry='3.413'/%3E%3C/g%3E%3Cpath fill='%231A202C' stroke='%234A5568' stroke-width='2' d='M104.18 1h503.381c14.911 0 27 12.088 27 27v346.15a3 3 0 0 1-3 3H80.18a3 3 0 0 1-3-3V28c0-14.912 12.088-27 27-27Z'/%3E%3Cpath fill='%23000' d='M79.607 25.413c0-12.15 9.85-22 22-22h508.526c12.15 0 22 9.85 22 22v338.403H79.607V25.413Z'/%3E%3Cpath fill='%23A3ACB1' d='M9 377.423a2 2 0 0 1 2-2h687a2 2 0 0 1 2 2v10.286H9v-10.286Z'/%3E%3Cpath fill='url(%23b)' d='M9 377.423a2 2 0 0 1 2-2h687a2 2 0 0 1 2 2v10.286H9v-10.286Z'/%3E%3Cpath fill='%23647279' d='M9 387.711h691l-1.8.361a399.988 399.988 0 0 1-78.755 7.83H83.931c-25.2 0-50.326-2.747-74.931-8.191Z'/%3E%3Cpath fill='url(%23c)' d='M9 387.711h691l-1.8.361a399.988 399.988 0 0 1-78.755 7.83H83.931c-25.2 0-50.326-2.747-74.931-8.191Z'/%3E%3Cpath fill='%2396A1A8' fill-rule='evenodd' d='M297.625 375.423c.353 5.336 4.812 9.556 10.26 9.556h93.916c5.449 0 9.907-4.22 10.26-9.556H297.625Z' clip-rule='evenodd'/%3E%3Cpath fill='url(%23d)' fill-rule='evenodd' d='M297.625 375.423c.353 5.336 4.812 9.556 10.26 9.556h93.916c5.449 0 9.907-4.22 10.26-9.556H297.625Z' clip-rule='evenodd'/%3E%3Cdefs%3E%3ClinearGradient id='b' x1='9' x2='700' y1='381.566' y2='381.566' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%230D1012'/%3E%3Cstop offset='.03' stop-color='%23CAD4DB'/%3E%3Cstop offset='.063' stop-color='%23242729'/%3E%3Cstop offset='.134' stop-color='%23A3ACB1'/%3E%3Cstop offset='.866' stop-color='%23A3ACB1'/%3E%3Cstop offset='.942' stop-color='%23242729'/%3E%3Cstop offset='.971' stop-color='%23CAD4DB'/%3E%3Cstop offset='.996' stop-color='%230D1012'/%3E%3C/linearGradient%3E%3ClinearGradient id='c' x1='354.5' x2='354.5' y1='387.711' y2='395.902' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%237A7F83'/%3E%3Cstop offset='1' stop-color='%230B0B0E'/%3E%3C/linearGradient%3E%3ClinearGradient id='d' x1='308.914' x2='402.829' y1='381.225' y2='381.225' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%233C3C3C'/%3E%3Cstop offset='.318' stop-color='%233C3C3C' stop-opacity='0'/%3E%3Cstop offset='.661' stop-color='%233C3C3C' stop-opacity='0'/%3E%3Cstop offset='1' stop-color='%23444'/%3E%3C/linearGradient%3E%3Cfilter id='a' width='708.393' height='94.826' x='.646' y='352.578' color-interpolation-filters='sRGB' filterUnits='userSpaceOnUse'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeBlend in='SourceGraphic' in2='BackgroundImageFix' result='shape'/%3E%3CfeGaussianBlur result='effect1_foregroundBlur_4992_10327' stdDeviation='22'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: top center;
      background-size: contain;

      @media (min-width: $screen-sm-min) {
        right: unset;
        left: unset;
        width: 100%;
        margin-right: unset;
        margin-left: unset;
      }

      video {
        position: absolute;
        top: 7.2%;
        left: 50%;
        width: 76%;
        max-width: 640px;
        transform: translateX(-50%);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// FeaturesFirstSection.vue